import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { BulkActionType } from '@core/bulk-actions-legacy';
import type { BulkAction } from '@core/bulk-actions-legacy';
import type { BulkOfferResponse } from '@core/bulk-actions-types';
import { I18nService } from '@core/i18n-legacy';
import { OfferBulkActions, OfferBulkActionsService } from '@core/offers-data';
import type { DialogConfirmOptions } from '@core/user-interface-legacy';
import { DialogService } from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import { ViewsManageEeoBulkService } from './views-manage-eeo-bulk.service';
import { ViewsManageReferralsBulkService } from './views-manage-referrals-bulk.service';

type ConfirmableActionType = BulkActionType.DeleteArchived | BulkActionType.RetrieveArchived;
type ConfirmableActionTexts = {
  title: string;
  message: string;
  agreeLabel: string;
  checkbox?: string;
};

@Injectable()
export class ViewsOffersBulkActionsExecutorService {
  private readonly store = inject(Store);
  private readonly manageEeoBulkService = inject(ViewsManageEeoBulkService);
  private readonly manageReferralsBulkService = inject(ViewsManageReferralsBulkService);
  private readonly dialog = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly offerBulkActionsService = inject(OfferBulkActionsService);

  private confirmDialogKeys: Record<ConfirmableActionType, ConfirmableActionTexts> = {
    [BulkActionType.DeleteArchived]: {
      title: 'app.offers.actions.bulk_delete_archived.confirm_title',
      message: 'app.offers.actions.bulk_delete_archived.confirm_message',
      agreeLabel: 'app.offers.actions.bulk_delete_archived.confirm_agree_label',
      checkbox: 'app.offers.actions.bulk_delete_archived.confirm_checkbox',
    },
    [BulkActionType.RetrieveArchived]: {
      title: 'app.offers.actions.bulk_retrieve_archived.confirm_title',
      message: 'app.offers.actions.bulk_retrieve_archived.confirm_message',
      agreeLabel: 'app.offers.actions.bulk_retrieve_archived.confirm_agree_label',
    },
  };

  public getExecutionStream(
    action: BulkAction,
    offerIds: number[],
  ): Observable<BulkOfferResponse | boolean> {
    const correlationId = generateUuid();
    const trackAsyncJob$ = this.offerBulkActionsService.trackByCorrelationId(correlationId);
    const bulkActionDefaultPayload = { payload: { correlationId, params: { ids: offerIds } } };
    const actionExecutionMap = {
      [BulkActionType.Follow]: () => {
        this.store.dispatch(OfferBulkActions.follow(bulkActionDefaultPayload));
        return trackAsyncJob$;
      },
      [BulkActionType.Unfollow]: () => {
        this.store.dispatch(OfferBulkActions.unfollow(bulkActionDefaultPayload));
        return trackAsyncJob$;
      },
      [BulkActionType.DeleteArchived]: () => {
        this.store.dispatch(OfferBulkActions.deleteArchived(bulkActionDefaultPayload));
        return trackAsyncJob$;
      },
      [BulkActionType.RetrieveArchived]: () => {
        this.store.dispatch(OfferBulkActions.retrieveArchived(bulkActionDefaultPayload));
        return trackAsyncJob$;
      },
      [BulkActionType.ManageEeo]: () => this.manageEeoBulkService.manageEeo$({ ids: offerIds }),
      [BulkActionType.ManageReferrals]: () =>
        this.manageReferralsBulkService.manageReferrals$({ ids: offerIds }),
    };
    const actionMethod = actionExecutionMap[<keyof typeof actionExecutionMap>action.id];

    return this.confirm$(action, offerIds.length).pipe(
      switchMap(confirmed => {
        if (confirmed) {
          return <Observable<BulkOfferResponse | boolean>>actionMethod();
        } else {
          return of(false);
        }
      }),
    );
  }

  private confirm$(action: BulkAction, count: number): Observable<boolean> {
    switch (action.id) {
      case BulkActionType.DeleteArchived:
      case BulkActionType.RetrieveArchived:
        return this.openConfirmDialog$(action.id, count);
      default:
        return of(true);
    }
  }

  private openConfirmDialog$(
    actionType: ConfirmableActionType,
    count: number,
  ): Observable<boolean> {
    const texts = this.getConfirmableActionTexts(actionType, count);
    const options: DialogConfirmOptions = {
      title: texts.title,
      message: texts.message,
      agreeLabel: texts.agreeLabel,
      ...(texts.checkbox && { checkboxes: [{ title: texts.checkbox }] }),
    };
    return actionType === BulkActionType.DeleteArchived
      ? this.dialog.error(options)
      : this.dialog.information(options);
  }

  private getConfirmableActionTexts(
    actionType: ConfirmableActionType,
    count: number,
  ): ConfirmableActionTexts {
    const keys = this.confirmDialogKeys[actionType];
    return {
      title: this.i18n.translate(keys.title, { count }),
      message: this.i18n.translate(keys.message, { count }),
      agreeLabel: this.i18n.translate(keys.agreeLabel, { count }),
      ...(keys.checkbox && { checkbox: this.i18n.translate(keys.checkbox!, { count }) }),
    };
  }
}
